<script setup lang="ts">
import { computed, shallowRef } from "vue";
import { useI18n } from "vue-i18n";
import BOptions from "@/components/common/bOptions.vue";
import ScreeningReviewDocumentPane from "@/components/recruitment/screening/review/ScreeningReviewDocumentPane.vue";

const { t } = useI18n();

const application = defineModel<any>({ required: true });

const emits = defineEmits<{
  openDocumentViewer: [file: { filePath: string; fileType: string }, fileName: string];
}>();

const documentTab = shallowRef<{ id: "CV" | "CL"; display: string }>({
  id: "CV",
  display: t("modules.recruitment.screening.review.resume"),
});

const documentTabs = [
  { id: "CV" as const, display: t("modules.recruitment.screening.review.resume") },
  { id: "CL" as const, display: t("modules.recruitment.screening.review.coverLetter") },
];

// A missing document shrinks to a narrow column so the other one gets the room
const gridColumns = computed(() => {
  const hasResume = Boolean(application.value?.resumePath?.filePath);
  const hasCoverLetter = Boolean(application.value?.coverLetterPath?.filePath);
  if (hasResume && !hasCoverLetter) return "lg:grid-cols-[minmax(0,1fr)_12rem]";
  if (!hasResume && hasCoverLetter) return "lg:grid-cols-[12rem_minmax(0,1fr)]";
  return "lg:grid-cols-2";
});
</script>

<template>
  <div class="flex min-h-0 min-w-0 flex-1 flex-col gap-2" data-cy="screening-review-documents">
    <div class="lg:hidden">
      <BOptions
        v-model="documentTab"
        :list-items="documentTabs"
        data-cy="screening-review-documents-tab"
      />
    </div>

    <div class="grid min-h-0 flex-1 grid-cols-1 gap-2" :class="gridColumns">
      <div class="min-h-0 h-full" :class="documentTab.id === 'CV' ? 'block' : 'hidden lg:block'">
        <ScreeningReviewDocumentPane
          v-model="application"
          type="CV"
          :title="t('modules.recruitment.screening.review.resume')"
          class="h-full"
          data-cy="screening-review-cv"
          @open-document-viewer="(file, fileName) => emits('openDocumentViewer', file, fileName)"
        />
      </div>
      <div class="min-h-0 h-full" :class="documentTab.id === 'CL' ? 'block' : 'hidden lg:block'">
        <ScreeningReviewDocumentPane
          v-model="application"
          type="CL"
          :title="t('modules.recruitment.screening.review.coverLetter')"
          class="h-full"
          data-cy="screening-review-cover-letter"
          @open-document-viewer="(file, fileName) => emits('openDocumentViewer', file, fileName)"
        />
      </div>
    </div>
  </div>
</template>
